import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { VisualizationType } from '@tempo-io/roadmap-dto';
import { VisualizationApi } from '@/lib/api/roadmapping/visualization';
import { roadmappingQueryKeys } from '@/lib/query-keys/roadmapping';

/**
 * Query options factory for fetching all visualizations for a view.
 */
export const getAllVisualizationsQueryOptions = (viewId: number) =>
  queryOptions({
    queryKey: roadmappingQueryKeys.visualization.all(viewId),
    queryFn: () => VisualizationApi.fetchAllVisualizations(viewId),
  });

/**
 * Query options factory for fetching a single visualization by ID.
 */
export const getVisualizationQueryOptions = (viewId: number, visualizationId: number) =>
  queryOptions({
    queryKey: roadmappingQueryKeys.visualization.detail(viewId, visualizationId),
    queryFn: () => VisualizationApi.fetchVisualization(viewId, visualizationId),
  });

/**
 * Query options factory for fetching a visualization by type.
 */
export const getVisualizationByTypeQueryOptions = (viewId: number, type: VisualizationType) =>
  queryOptions({
    queryKey: roadmappingQueryKeys.visualization.byType(viewId, type),
    queryFn: () => VisualizationApi.fetchVisualizationByType(viewId, type),
  });

export const useAllVisualizations = (viewId: number) => {
  return useQuery(getAllVisualizationsQueryOptions(viewId));
};

export const useAllVisualizationsData = (viewId: number) => {
  return useSuspenseQuery(getAllVisualizationsQueryOptions(viewId));
};

export const useVisualization = (viewId: number, visualizationId: number) => {
  return useQuery(getVisualizationQueryOptions(viewId, visualizationId));
};

export const useVisualizationData = (viewId: number, visualizationId: number) => {
  return useSuspenseQuery(getVisualizationQueryOptions(viewId, visualizationId));
};

export const useVisualizationByType = (viewId: number, type: VisualizationType) => {
  return useQuery(getVisualizationByTypeQueryOptions(viewId, type));
};

export const useVisualizationByTypeData = (viewId: number, type: VisualizationType) => {
  return useSuspenseQuery(getVisualizationByTypeQueryOptions(viewId, type));
};
